// Debug configuration for request/response logging
export const DEBUG_CONFIG = {
  // Set to true to enable detailed logging
  ENABLE_REQUEST_LOGGING: true,
  ENABLE_RESPONSE_LOGGING: true,
  ENABLE_SCOPE_LOGGING: true,
  ENABLE_SESSION_LOGGING: true,
  
  // Log levels
  LOG_LEVEL: 'debug' as 'debug' | 'info' | 'warn' | 'error',
  
  // Prevent duplicate logging
  LOGGED_REQUESTS: new Set<string>(),
};

// Debug logging utility
export class DebugLogger {
  private static shouldLog(level: string): boolean {
    const levels = ['debug', 'info', 'warn', 'error'];
    const currentLevelIndex = levels.indexOf(DEBUG_CONFIG.LOG_LEVEL);
    const messageLevelIndex = levels.indexOf(level);
    return messageLevelIndex >= currentLevelIndex;
  }

  static debug(message: string, data?: any) {
    if (DEBUG_CONFIG.ENABLE_REQUEST_LOGGING && this.shouldLog('debug')) {
      console.log(`🔍 [DEBUG] ${message}`, data || '');
    }
  }

  static info(message: string, data?: any) {
    if (this.shouldLog('info')) {
      console.log(`ℹ️ [INFO] ${message}`, data || '');
    }
  }

  static warn(message: string, data?: any) {
    if (this.shouldLog('warn')) {
      console.warn(`⚠️ [WARN] ${message}`, data || '');
    }
  }

  static error(message: string, data?: any) {
    if (this.shouldLog('error')) {
      console.error(`❌ [ERROR] ${message}`, data || '');
    }
  }

  static request(method: string, url: string, headers?: any, body?: any) {
    if (DEBUG_CONFIG.ENABLE_REQUEST_LOGGING) {
      const requestKey = `${method}:${url}`;
      if (!DEBUG_CONFIG.LOGGED_REQUESTS.has(requestKey)) {
        console.log(`📤 [REQUEST] ${method} ${url}`);
        if (headers) {
          console.log('📋 Headers:', JSON.stringify(headers, null, 2));
        }
        if (body) {
          console.log('📦 Body:', JSON.stringify(body, null, 2));
        }
        DEBUG_CONFIG.LOGGED_REQUESTS.add(requestKey);
      }
    }
  }

  static response(method: string, url: string, status: number, statusText: string, data?: any) {
    if (DEBUG_CONFIG.ENABLE_RESPONSE_LOGGING) {
      const requestKey = `${method}:${url}`;
      if (DEBUG_CONFIG.LOGGED_REQUESTS.has(requestKey)) {
        console.log(`📥 [RESPONSE] ${status} ${statusText}`);
        if (data) {
          console.log('📦 Response Data:', JSON.stringify(data, null, 2));
        }
        DEBUG_CONFIG.LOGGED_REQUESTS.delete(requestKey); // Clear after logging response
      }
    }
  }

  static scope(message: string, context?: any) {
    if (DEBUG_CONFIG.ENABLE_SCOPE_LOGGING) {
      console.log(`🎯 [SCOPE] ${message}`, context || '');
    }
  }

  static session(message: string, data?: any) {
    if (DEBUG_CONFIG.ENABLE_SESSION_LOGGING) {
      console.log(`🔐 [SESSION] ${message}`, data || '');
    }
  }

  // Clear logged requests to allow re-logging in case of retries
  static clearLoggedRequests() {
    DEBUG_CONFIG.LOGGED_REQUESTS.clear();
  }
}
